<template>
  <div class="activity-stage q-and-a">
    <div v-if="showActivity">
      <div class="left-side">
        <img :src="currentRound.contentImage?.image.src" />
      </div>
      <div class="right-side">
        <div class="q-a-instructions">
          <span v-text="instructions"></span>
        </div>
        <div class="steps-container">
          <div class="step-wrapper step-question">
            <span class="label">Question:</span>
            <blinking-cursor-text
              :text="currentRound.steps[0].question"
              :text-step="0"
              :round-id="currentRound.roundIndex"
            >
            </blinking-cursor-text>
          </div>
          <div class="step-wrapper step-answer">
            <span class="label">Answer:</span>
            <blinking-cursor-text
              :text="currentRound.steps[0].answer"
              :text-step="1"
              :round-id="currentRound.roundIndex"
            >
            </blinking-cursor-text>
          </div>
        </div>
      </div>
    </div>

    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    BlinkingCursorText
  },

  data() {
    return {
      instructions: 'Type the question and answer.',
      showActivity: false,
      completed: 0
    };
  },

  methods: {
    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const container = document.querySelector('.q-and-a');
      container.style.height = `calc(100% - ${keyboard.offsetHeight + 20}px)`;
    },

    async loadActivity() {
      const activityState = this.getActivityState();
      await this.loadImages(activityState.rounds.map(({ contentImage }) => contentImage.image.src));
      this.createRounds(activityState);
    },

    async loadImages(images) {
      this.queueImage(images);
      this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds: rounds.map((round, roundIndex) => ({
          ...round,
          roundIndex
        })),
        instructions: {
          delay: 0,
          voice: `${
            this.voiceInstructionsPrefix
          }/questionsandanswers-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    fadeInContainer() {
      TweenMax.to('.q-and-a', 1, {
        opacity: 1,
        onComplete() {
          TweenMax.to('.step-question', 1, { opacity: 1 });
        }
      });
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.showActivity = true;
      this.overwriteProgressText({
        currentText: this.currentRound.steps[0].question
      });
      this.$nextTick(() => {
        this.setContainerHeight();
        this.$nextTick(this.fadeInContainer);
      });
    },

    handleNewRound() {
      const self = this;
      self.completed = 0;

      TweenMax.to('.q-and-a', 0.5, {
        opacity: 0,
        onComplete() {
          // hide question and answer from previous round
          TweenMax.to('.step-wrapper', 0, { opacity: 0 });

          self.setupNewRound();
          self.overwriteProgressText({
            currentText: self.currentRound.steps[0].question
          });
          self.$nextTick(() => self.fadeInContainer());
        }
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();
        this.completed += 1;

        if (this.completed === 1) {
          this.overwriteProgressText({
            currentText: this.currentRound.steps[0].answer
          });
          TweenMax.to('.step-answer', 0.5, { opacity: 1 });
        } else if (this.completed === 2) {
          if (this.isEndOfActivity) {
            this.playCongrats();
            TweenMax.delayedCall(1, () => this.completeActivity(false));
            return;
          }

          // start new round otherwise
          this.handleNewRound();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.q-and-a,
.step-question,
.step-answer {
  opacity: 0;
}

.step-wrapper p {
  font-size: 30px;
}
</style>
